import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CrownSimpleIcon, InfoIcon } from "@phosphor-icons/react";

/**
 * FeePreview — live, authoritative breakdown of what the seller will net
 * for a given gross sale amount. Calls `/api/sellers/fee-preview` so the
 * math always matches what `release_escrow()` actually credits.
 *
 * Shows three numbers + their narrative:
 *   - Gross sale (what the buyer pays)
 *   - Platform fee (commission + processing)
 *   - Seller net (what lands in the payout ledger)
 *
 * If the seller is a Founder Seller within their first 14 days, also shows
 * a callout for the post-perk economics ("After your free window ends...").
 *
 * Debounced 250ms to avoid hammering the API on every keystroke.
 */
export default function FeePreview({ amount }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const value = parseFloat(amount);
    if (!isFinite(value) || value <= 0) {
      setPreview(null);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/sellers/fee-preview?amount=${value}`);
        setPreview(data);
      } catch {
        setPreview(null);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [amount]);

  if (!preview) {
    return (
      <div
        className="mt-3 rounded-lg border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-500 flex items-center gap-2"
        data-testid="fee-preview-empty"
      >
        <InfoIcon size={14} />
        Enter a price to see your exact payout after Bidorah&apos;s fee.
      </div>
    );
  }

  const fmt = (n) =>
    `$${Number(n).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  const pct = (n) => `${(Number(n) * 100).toFixed(1).replace(/\.0$/, "")}%`;

  const founder = preview.founder_active;
  const segment = preview.segment;

  return (
    <div
      className={`mt-3 rounded-lg border p-3 text-sm transition-colors ${
        founder
          ? "border-amber-300 bg-amber-50/60"
          : "border-zinc-200 bg-zinc-50"
      } ${loading ? "opacity-70" : ""}`}
      data-testid="fee-preview"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          {founder && (
            <CrownSimpleIcon
              size={14}
              weight="fill"
              className="text-amber-500"
            />
          )}
          <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-600">
            {founder
              ? `Founder perk · 0% commission · ${preview.founder_days_remaining}d left`
              : segment === "wholesale"
              ? "Wholesale · 4.5% commission"
              : "Retail · 5% commission"}
          </span>
        </div>
        {loading && (
          <span className="text-[10px] text-zinc-400">calculating…</span>
        )}
      </div>

      <div className="space-y-1.5">
        <div className="flex items-baseline justify-between text-zinc-700">
          <span>Gross sale</span>
          <span className="tabular-nums">{fmt(preview.amount)}</span>
        </div>
        <div className="flex items-baseline justify-between text-zinc-500 text-xs">
          <span>
            Bidorah fee · {pct(preview.commission_pct)} +{" "}
            {pct(preview.processing_pct)} +{" "}
            {fmt(preview.processing_fixed)}
          </span>
          <span className="tabular-nums">−{fmt(preview.platform_fee)}</span>
        </div>
        <div className="flex items-baseline justify-between pt-1.5 mt-1.5 border-t border-zinc-200">
          <span className="font-semibold text-zinc-900">You receive</span>
          <span
            className={`font-display font-black text-xl tabular-nums ${
              founder ? "text-amber-700" : "text-[#1D78AB]"
            }`}
            data-testid="fee-preview-seller-net"
          >
            {fmt(preview.seller_net)}
          </span>
        </div>
      </div>

      {founder && (
        <div
          className="mt-3 pt-3 border-t border-amber-200 text-[11px] text-amber-900/80"
          data-testid="fee-preview-post-founder"
        >
          After your Founder window ends, the same listing nets{" "}
          <span className="font-bold">{fmt(preview.post_founder_net)}</span>{" "}
          (
          {segment === "wholesale" ? "4.5% wholesale" : "5% retail"} commission
          + processing).
        </div>
      )}
      <div className="mt-2 text-[10px] text-zinc-400 leading-snug">
        Tips you receive on stream pass through 100% — never deducted.
      </div>
    </div>
  );
}
